import { useEffect, useState } from 'react';

import { useApi, type TileListResponse, type TileSetting } from './client';

/**
 * Loads tile settings from /api/v1/admin/tiles and exposes them as a map
 * keyed by tile_id. Callers fall back to their hardcoded defaults (e.g.
 * i18n strings) when a tile_id isn't in the map.
 *
 * Used by HomeView to show admin-edited titles, descriptions, and photos
 * for the home-screen action cards. The hook is also used by the
 * /admin/appearance editor page to populate the form.
 *
 * No caching beyond React state — the hook fires the GET each time it
 * mounts. For the home view that's once per session (after sign-in).
 * If we ever need cross-component caching we'd lift this into a context.
 */

export type TileSettingsMap = Map<string, TileSetting>;

type HookState =
  | { kind: 'loading' }
  | { kind: 'ok'; map: TileSettingsMap }
  | { kind: 'error'; message: string };

export function useTileSettings(): HookState {
  const callApi = useApi();
  const [state, setState] = useState<HookState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await callApi<TileListResponse>('/admin/tiles');
        if (cancelled) return;
        const map = new Map<string, TileSetting>();
        for (const row of data.rows) {
          map.set(row.tile_id, row);
        }
        setState({ kind: 'ok', map });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load tile settings';
        setState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
